import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as XLSX from 'xlsx';

/**
 * Import result interface
 */
export interface ImportResult<T = any> {
  success: boolean;
  data: T[];
  errors: ImportError[];
  warnings: string[];
}

export interface ImportError {
  row: number;
  column?: string;
  message: string;
}

export interface ImportColumnDef {
  key: string;
  label: string;
  type?: string;
  selectOptions?: Array<{ value: string; label: string }>;
  required?: boolean;
  isUnique?: boolean;
  fieldName?: string;
  min?: number;
  max?: number;
  pattern?: string;
}

export interface ValidationResult {
  validRows: Record<string, any>[];
  invalidRows: InvalidRowEntry[];
  totalRows: number;
}

export interface InvalidRowEntry {
  rowIndex: number;
  data: Record<string, any>;
  errors: string[];
}

export interface CellValidationError {
  rowIndex: number;
  columnLabel: string;
  fieldName: string;
  value: any;
  message: string;
  expected?: string;
}

export interface ImportValidationResult {
  totalRows: number;
  validRowCount: number;
  invalidRowCount: number;
  errorsByColumn: Record<string, CellValidationError[]>;
  invalidRowIndices: Set<number>;
}

/**
 * Child Table Import Service
 * Handles CSV and JSON data import
 * Framework-agnostic - can be reused in React
 */
@Injectable({
  providedIn: 'root',
})
export class ChildTableImportService {
  private importComplete$ = new Subject<ImportResult>();
  
  readonly onImportComplete = this.importComplete$.asObservable();

  /**
   * Generate and download an Excel template for the given columns.
   * ENUM/select columns are annotated with a comment listing valid values.
   * A separate "Valid Values" sheet is included for reference.
   */
  downloadExcelTemplate(columns: ImportColumnDef[], filename = 'template.xlsx'): void {
    const workbook = XLSX.utils.book_new();

    // ── Sheet 1: "Template" (the main data-entry sheet, always first) ──
    // Row 1: column headers (clean labels, auto-mapped on re-upload)
    // Row 2: guide row — format hints / all valid options (users delete before importing)
    // Rows 3+: empty for user data entry
    const headers = columns.map(c => c.label);
    
    // Build the guide row (row 2)
    const guideRow = columns.map(c => {
      if (c.selectOptions && c.selectOptions.length > 0) {
        // For dropdowns, list ALL valid options in the cell
        return c.selectOptions.map(o => o.label || o.value).join(', ');
      }
      return this.getTypeDescription(c); // e.g. "Date (YYYY-MM-DD)"
    });

    // Create sheet with headers + guide row
    const wsData = [headers, guideRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Add cell comments (notes) to headers for extra guidance
    if (!ws['!comments']) ws['!comments'] = [];
    columns.forEach((col, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx }); // Header row
      const hint = this.getHeaderHint(col);
      if (hint) {
        if (!ws[cellRef]) ws[cellRef] = { v: headers[idx], t: 's' }; // Ensure cell exists
        if (!ws[cellRef].c) ws[cellRef].c = [];
        ws[cellRef].c.push({ a: 'System', t: hint });
      }
    });

    // Add Data Validation (dropdowns) for ENUM columns
    // Note: SheetJS Pro supports data validation fully. Community edition support is limited but
    // we can structure the "Valid Values" sheet to be friendly for manual validation setup if needed.
    // For now, we populate the guide row which is the most reliable cross-version help.

    // Set column widths for better readability
    ws['!cols'] = columns.map(c => ({ wch: Math.max(c.label.length + 5, 20) }));

    XLSX.utils.book_append_sheet(workbook, ws, 'Template');

    // ── Sheet 2: "Valid Options" (reference sheet) ──
    // Lists all dropdown options for easy copy-pasting
    const validOptionsData: any[][] = [];
    const validOptionsHeaders: string[] = [];
    
    const enumCols = columns.filter(c => c.selectOptions && c.selectOptions.length > 0);
    if (enumCols.length > 0) {
      validOptionsHeaders.push(...enumCols.map(c => c.label));
      validOptionsData.push(validOptionsHeaders);

      // Find max number of options to determine rows
      const maxOpts = Math.max(...enumCols.map(c => c.selectOptions?.length || 0));
      
      for (let i = 0; i < maxOpts; i++) {
        const row: string[] = [];
        for (const col of enumCols) {
          const opt = col.selectOptions?.[i];
          row.push(opt ? (opt.label || opt.value) : '');
        }
        validOptionsData.push(row);
      }

      const wsOptions = XLSX.utils.aoa_to_sheet(validOptionsData);
      wsOptions['!cols'] = enumCols.map(() => ({ wch: 30 }));
      XLSX.utils.book_append_sheet(workbook, wsOptions, 'Valid Options');
    }

    // Write file
    XLSX.writeFile(workbook, filename);
  }

  /**
   * Parse an Excel (.xlsx/.xls) file into an array of row objects.
   * Uses the first sheet only; first row is treated as headers.
   */
  parseXLSX<T>(
    arrayBuffer: ArrayBuffer,
    columnMapping: Record<string, string>,
  ): { result: ImportResult<T>; headers: string[] } {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      if (workbook.SheetNames.length === 0) {
        return {
          result: { success: false, data: [], errors: [{ row: 0, message: 'Excel file is empty' }], warnings: [] },
          headers: []
        };
      }

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to array of arrays
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];

      if (jsonData.length === 0) {
        return {
          result: { success: false, data: [], errors: [{ row: 0, message: 'Sheet is empty' }], warnings: [] },
          headers: []
        };
      }

      // Row 0 is headers
      const headers = (jsonData[0] || []).map(h => String(h).trim());
      
      // Map headers to field keys
      const mappedHeaders: string[] = [];
      for (const header of headers) {
        const mappedKey = columnMapping[header.toLowerCase()] || header;
        mappedHeaders.push(mappedKey);
      }

      const data: T[] = [];
      const errors: ImportError[] = [];
      const warnings: string[] = [];

      // Start from row 1 (data)
      for (let i = 1; i < jsonData.length; i++) {
        const rowValues = jsonData[i];
        // Skip empty rows
        if (!rowValues || rowValues.every(v => v === '')) continue;

        const rowObj: any = {};
        for (let j = 0; j < mappedHeaders.length; j++) {
          const key = mappedHeaders[j];
          if (key) {
            rowObj[key] = this.parseValue(String(rowValues[j] || ''));
          }
        }
        data.push(rowObj as T);
      }

      if (data.length === 0) {
        return {
          result: { success: false, data: [], errors: [], warnings: ['No data found in file'] },
          headers
        };
      }

      return {
        result: { success: true, data, errors, warnings },
        headers
      };

    } catch (err) {
      return {
        result: {
          success: false,
          data: [],
          errors: [{ row: 0, message: `Excel parsing failed: ${err}` }],
          warnings: []
        },
        headers: []
      };
    }
  }

  /**
   * Parse CSV string to array of objects
   */
  parseCSV<T>(
    csvString: string,
    columnMapping: Record<string, string>
  ): ImportResult<T> {
    const errors: ImportError[] = [];
    const warnings: string[] = [];
    const data: T[] = [];

    try {
      const lines = csvString.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length === 0) {
        return { success: false, data: [], errors: [{ row: 0, message: 'Empty file' }], warnings: [] };
      }

      // Parse header row
      const headers = this.parseCSVLine(lines[0]);
      
      // Validate headers against column mapping
      const mappedHeaders: string[] = [];
      for (const header of headers) {
        const mappedKey = columnMapping[header.toLowerCase()] || header;
        mappedHeaders.push(mappedKey);
      }

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        try {
          const values = this.parseCSVLine(lines[i]);
          const row: any = {};
          
          for (let j = 0; j < mappedHeaders.length; j++) {
            row[mappedHeaders[j]] = this.parseValue(values[j] || '');
          }
          
          data.push(row as T);
        } catch (err) {
          errors.push({ row: i + 1, message: `Failed to parse row: ${err}` });
        }
      }

      if (errors.length > 0 && data.length === 0) {
        return { success: false, data: [], errors, warnings };
      }

      if (errors.length > 0) {
        warnings.push(`${errors.length} row(s) had parsing errors and were skipped`);
      }

      return { success: true, data, errors, warnings };
    } catch (err) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: `CSV parsing failed: ${err}` }],
        warnings: [],
      };
    }
  }

  /**
   * Parse JSON string to array of objects
   */
  parseJSON<T>(jsonString: string): ImportResult<T> {
    try {
      const parsed = JSON.parse(jsonString);
      
      if (Array.isArray(parsed)) {
        return { success: true, data: parsed as T[], errors: [], warnings: [] };
      }
      
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.data)) {
        return { success: true, data: parsed.data as T[], errors: [], warnings: [] };
      }
      
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: 'JSON must be an array or contain a "data" array property' }],
        warnings: [],
      };
    } catch (err) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: `Invalid JSON: ${err}` }],
        warnings: [],
      };
    }
  }

  /**
   * Read file and return contents as string
   */
  readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Import from file
   */
  async importFromFile<T>(
    file: File,
    columnMapping: Record<string, string> = {}
  ): Promise<ImportResult<T>> {
    const content = await this.readFile(file);
    const extension = file.name.split('.').pop()?.toLowerCase();

    let result: ImportResult<T>;

    if (extension === 'json') {
      result = this.parseJSON<T>(content);
    } else if (extension === 'csv' || extension === 'txt') {
      result = this.parseCSV<T>(content, columnMapping);
    } else {
      result = {
        success: false,
        data: [],
        errors: [{ row: 0, message: `Unsupported file type: ${extension}` }],
        warnings: [],
      };
    }

    this.importComplete$.next(result);
    return result;
  }

  /**
   * Generate sample CSV template
   */
  generateCSVTemplate(columns: { key: string; label: string }[]): string {
    const headers = columns.map((c) => c.label).join(',');
    const sample = columns.map((c) => `"Sample ${c.label}"`).join(',');
    return `${headers}\n${sample}`;
  }

  /**
   * Download template as CSV file
   */
  downloadTemplate(columns: { key: string; label: string }[], filename = 'template.csv'): void {
    const csv = this.generateCSVTemplate(columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(url);
  }

  /**
   * Download actual table data as CSV with column headers
   */
  downloadCSV(
    columns: { key: string; label: string; type?: string }[],
    rows: Record<string, any>[],
    filename: string
  ): void {
    const headers = columns.map((c) => this.escapeCsvField(c.label)).join(',');
    const dataLines = rows.map((row) =>
      columns.map((c) => this.escapeCsvField(String(row[c.key] ?? ''))).join(',')
    );
    const csv = [headers, ...dataLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Validate import data against column definitions before committing.
   * Returns per-column errors with human-readable messages.
   */
  validateImportData(
    data: Record<string, any>[],
    columns: Array<ImportColumnDef & { fieldName: string }>,
    mappings: Array<{ csvHeader: string; fieldName: string }>,
  ): ImportValidationResult {
    const errorsByColumn: Record<string, CellValidationError[]> = {};
    const invalidRowIndices = new Set<number>();

    for (const mapping of mappings) {
      if (!mapping.fieldName) continue;
      const col = columns.find(c => c.fieldName === mapping.fieldName);
      if (!col) continue;

      const columnErrors: CellValidationError[] = [];

      for (let rowIdx = 0; rowIdx < data.length; rowIdx++) {
        const rawValue = data[rowIdx][mapping.csvHeader];
        const strValue = rawValue == null ? '' : String(rawValue).trim();
        const error = this.validateCell(strValue, col, rowIdx, mapping.csvHeader);
        if (error) {
          columnErrors.push(error);
          invalidRowIndices.add(rowIdx);
        }
      }

      if (columnErrors.length > 0) {
        errorsByColumn[mapping.csvHeader] = columnErrors;
      }
    }

    return {
      totalRows: data.length,
      validRowCount: data.length - invalidRowIndices.size,
      invalidRowCount: invalidRowIndices.size,
      errorsByColumn,
      invalidRowIndices,
    };
  }

  private validateCell(
    value: string,
    col: ImportColumnDef & { fieldName: string },
    rowIndex: number,
    csvHeader: string,
  ): CellValidationError | null {
    const base = { rowIndex, columnLabel: col.label, fieldName: col.fieldName, value };

    if (col.required && !value) {
      return { ...base, message: 'This field is required', expected: 'Non-empty value' };
    }
    if (!value) return null;

    if (col.selectOptions && col.selectOptions.length > 0) {
      if (col.type === 'multiselect') {
        const values = value.split(',').map(v => v.trim()).filter(Boolean);
        for (const v of values) {
          const valid = col.selectOptions.some(o =>
            o.label.toLowerCase() === v.toLowerCase() || o.value.toLowerCase() === v.toLowerCase());
          if (!valid) {
            const opts = col.selectOptions.map(o => o.label || o.value).join(', ');
            return { ...base, value: v, message: `"${v}" is not a valid option`, expected: opts };
          }
        }
      } else {
        const valid = col.selectOptions.some(o =>
          o.label.toLowerCase() === value.toLowerCase() || o.value.toLowerCase() === value.toLowerCase());
        if (!valid) {
          const opts = col.selectOptions.map(o => o.label || o.value).join(', ');
          return { ...base, message: `"${value}" is not a valid option`, expected: opts };
        }
      }
      return null;
    }

    switch (col.type) {
      case 'checkbox': {
        if (!['true', 'false', 'yes', 'no', '1', '0'].includes(value.toLowerCase())) {
          return { ...base, message: `"${value}" is not a valid boolean`, expected: 'true, false, yes, no, 1, or 0' };
        }
        break;
      }
      case 'number':
      case 'currency': {
        const num = Number(value);
        if (Number.isNaN(num)) return { ...base, message: `"${value}" is not a valid number`, expected: 'Numeric value' };
        if (col.min !== undefined && num < col.min) return { ...base, message: `${num} is below minimum`, expected: `At least ${col.min}` };
        if (col.max !== undefined && num > col.max) return { ...base, message: `${num} exceeds maximum`, expected: `At most ${col.max}` };
        break;
      }
      case 'integer': {
        const num = Number(value);
        if (Number.isNaN(num) || !Number.isInteger(num)) return { ...base, message: `"${value}" is not a whole number`, expected: 'Integer' };
        if (col.min !== undefined && num < col.min) return { ...base, message: `${num} is below minimum`, expected: `At least ${col.min}` };
        if (col.max !== undefined && num > col.max) return { ...base, message: `${num} exceeds maximum`, expected: `At most ${col.max}` };
        break;
      }
      case 'date': {
        if (Number.isNaN(new Date(value).getTime())) return { ...base, message: `"${value}" is not a valid date`, expected: 'YYYY-MM-DD' };
        break;
      }
      case 'datetime': {
        if (Number.isNaN(new Date(value).getTime())) return { ...base, message: `"${value}" is not a valid date-time`, expected: 'YYYY-MM-DD HH:MM' };
        break;
      }
      case 'email': {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { ...base, message: `"${value}" is not a valid email`, expected: 'user@example.com' };
        break;
      }
    }

    if (col.pattern) {
      try {
        if (!new RegExp(col.pattern).test(value)) return { ...base, message: `"${value}" does not match required format`, expected: `Pattern: ${col.pattern}` };
      } catch { /* invalid regex */ }
    }

    return null;
  }

  // Private helpers

  private escapeCsvField(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
    }

    result.push(current.trim());
    return result;
  }

  private parseValue(value: string): string | number | boolean {
    // Try to parse as number
    const num = Number(value);
    if (!isNaN(num) && value.trim() !== '') {
      return num;
    }

    // Try to parse as boolean
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;

    // Return as string
    return value;
  }

  private getTypeDescription(col: ImportColumnDef): string {
    if (col.selectOptions && col.selectOptions.length > 0) {
      const vals = col.selectOptions.map(o => o.label || o.value).join(', ');
      const prefix = col.type === 'multiselect' ? 'Comma-separated from' : 'One of';
      return `${prefix}: ${vals}`;
    }
    let desc = '';
    switch (col.type) {
      case 'date': desc = 'Date format: YYYY-MM-DD'; break;
      case 'datetime': desc = 'Date-time format: YYYY-MM-DD HH:MM'; break;
      case 'number': desc = 'Numeric (decimal allowed)'; break;
      case 'integer': desc = 'Whole number only'; break;
      case 'currency': desc = 'Numeric (decimal allowed, no $ symbol)'; break;
      case 'checkbox': desc = 'true or false'; break;
      case 'email': desc = 'Valid email address'; break;
      case 'phone': desc = 'Phone number string'; break;
      case 'file':
      case 'attachment': desc = 'Not supported in CSV/Excel import'; break;
      default: desc = 'Any text'; break;
    }
    const constraints: string[] = [];
    if (col.min !== undefined) constraints.push(`min: ${col.min}`);
    if (col.max !== undefined) constraints.push(`max: ${col.max}`);
    if (col.pattern) constraints.push(`pattern: ${col.pattern}`);
    if (constraints.length > 0) desc += ` [${constraints.join(', ')}]`;
    return desc;
  }

  private getHeaderHint(col: ImportColumnDef): string | null {
    const parts: string[] = [];
    if (col.required) parts.push('REQUIRED');
    if (col.isUnique) parts.push('Must be unique');
    if (col.selectOptions && col.selectOptions.length > 0) {
      const all = col.selectOptions.map(o => o.label || o.value).join(', ');
      parts.push(`Valid options: ${all}`);
    } else {
      switch (col.type) {
        case 'date': parts.push('Format: YYYY-MM-DD (e.g. 2024-06-15)'); break;
        case 'datetime': parts.push('Format: YYYY-MM-DD HH:MM (e.g. 2024-06-15 14:30)'); break;
        case 'number':
        case 'currency': parts.push('Numeric values only — no currency symbols'); break;
        case 'integer': parts.push('Whole numbers only'); break;
        case 'checkbox': parts.push('Enter: true or false'); break;
        case 'email': parts.push('Valid email address (e.g. user@company.com)'); break;
        case 'phone': parts.push('Phone number (e.g. 555-000-0000)'); break;
        default: break;
      }
    }
    if (col.min !== undefined) parts.push(`Minimum: ${col.min}`);
    if (col.max !== undefined) parts.push(`Maximum: ${col.max}`);
    return parts.length > 0 ? parts.join('\n') : null;
  }
}
